/**
 * HexGrid - マップエディタ用ヘクスグリッドコンポーネント
 *
 * MapDisplay.tsx からヘクス描画ロジックを流用し、
 * 編集モード用にクリックハンドラを追加。
 * ドラッグによるパン機能とズーム機能を実装。
 */

import React, { useMemo, useState, useCallback, useRef } from 'react';
import { renderTileTracks } from '../map/MapDisplay';
import { UNOWNED_TRACK_COLOR } from '../../types';

// ============================================================================
// 型定義
// ============================================================================

export type EditorTerrainType = 'plains' | 'mountains' | 'river' | 'water';

export interface EditorCity {
  name: string;
  colors: ('red' | 'blue' | 'yellow' | 'purple' | 'black')[];  // 複数色をサポート
  diceNumbers: number[];  // 空〜6種のダイス目をサポート
  diceColors: ('white' | 'black')[];  // 白/黒/両方をサポート
}

export interface EditorTown {
  name: string;
  urbanizable: boolean;
}

/** 敷設済み線路（エディタ用） */
export interface EditorPreLaidTrack {
  tileType: string;
  rotation: 0 | 1 | 2 | 3 | 4 | 5;
}

export interface EditorHex {
  q: number;
  r: number;
  terrain: EditorTerrainType;
  city?: EditorCity;
  town?: EditorTown;
  /** 壁がある方向（0-5） */
  blockedEdges?: number[];
  /** 敷設済み線路 */
  preLaidTrack?: EditorPreLaidTrack;
}

/** フェリーリンク（離れたヘクス間） */
export interface EditorFerryLink {
  id: string;
  from: { q: number; r: number };
  to: { q: number; r: number };
  cost: number;
}

/** 隣接リンク（隣接ヘクス間） */
export interface EditorAdjacentLink {
  id: string;
  from: { q: number; r: number };
  to: { q: number; r: number };
  cost: number;
}

/** 仮配置情報（敷設済み線路） */
export interface TemporaryTrackPlacement {
  q: number;
  r: number;
  tileType: string;
  rotation: number;
}

export interface HexGridProps {
  hexes: EditorHex[];
  hexSize?: number;
  selectedHex?: { q: number; r: number } | null;
  onHexClick?: (hex: EditorHex) => void;
  onHexRightClick?: (hex: EditorHex) => void;
  orientation?: 'portrait' | 'landscape';
  onToggleOrientation?: () => void;
  // ズーム機能
  zoomLevel?: number;
  onZoomIn?: () => void;
  onZoomOut?: () => void;
  // リンク
  ferryLinks?: EditorFerryLink[];
  adjacentLinks?: EditorAdjacentLink[];
  // リンク選択用（最初のヘクスが選択されているとき）
  linkStartHex?: { q: number; r: number } | null;
  // 仮配置（敷設済み線路）
  temporaryTrack?: TemporaryTrackPlacement | null;
}

// ============================================================================
// ユーティリティ関数（MapDisplay.tsx から流用）
// ============================================================================

/**
 * Axial座標をピクセル座標に変換
 */
function axialToPixel(q: number, r: number, hexSize: number): { x: number; y: number } {
  const x = hexSize * (Math.sqrt(3) * q + (Math.sqrt(3) / 2) * r);
  const y = hexSize * ((3 / 2) * r);
  return { x, y };
}

/**
 * ヘクスの頂点座標を計算（Pointy-top orientation）
 */
function getHexPoints(centerX: number, centerY: number, hexSize: number): string {
  const points: [number, number][] = [];
  for (let i = 0; i < 6; i++) {
    const compassAngle = (i * 60) * Math.PI / 180;
    const x = centerX + hexSize * Math.sin(compassAngle);
    const y = centerY - hexSize * Math.cos(compassAngle);
    points.push([x, y]);
  }
  return points.map((p) => p.join(',')).join(' ');
}

/**
 * 地形に応じたfill色を返す
 */
function getTerrainFill(terrain: EditorTerrainType): string {
  switch (terrain) {
    case 'plains':
      return 'hsl(var(--terrain-grass))';
    case 'mountains':
      return 'hsl(var(--terrain-mountain))';
    case 'river':
    case 'water':
      return 'hsl(var(--terrain-water))';
    default:
      return 'hsl(var(--muted))';
  }
}

/**
 * 都市色に応じたfill色を返す
 */
function getCityColorFill(color: string): string {
  switch (color) {
    case 'red':
      return 'hsl(var(--goods-red))';
    case 'blue':
      return 'hsl(var(--goods-blue))';
    case 'yellow':
      return 'hsl(var(--goods-yellow))';
    case 'purple':
      return 'hsl(var(--goods-purple))';
    case 'black':
      return 'hsl(var(--goods-black))';
    default:
      return 'hsl(var(--muted))';
  }
}

/**
 * 同じヘクスペア間のリンク数をカウントし、インデックスを取得
 * 曲線を左右対称に分散させるためのオフセット係数も返す
 */
function getLinkOffset(
  links: EditorFerryLink[],
  currentLink: EditorFerryLink
): { offsetMultiplier: number; total: number } {
  // 同じヘクスペアを持つリンクをフィルタ
  const sameHexLinks = links.filter(link => {
    const sameDirection =
      link.from.q === currentLink.from.q && link.from.r === currentLink.from.r &&
      link.to.q === currentLink.to.q && link.to.r === currentLink.to.r;
    const reverseDirection =
      link.from.q === currentLink.to.q && link.from.r === currentLink.to.r &&
      link.to.q === currentLink.from.q && link.to.r === currentLink.from.r;
    return sameDirection || reverseDirection;
  });

  const index = sameHexLinks.findIndex(link => link.id === currentLink.id);
  const total = sameHexLinks.length;

  // 曲線を逆方向の弧で分散させる（正負で交互）
  // 1本: +1.0
  // 2本: +1.0, -1.0（逆方向の弧）
  // 3本: +1.0, 0, -1.0
  // 4本以上: 正から負へ均等分布
  let offsetMultiplier: number;
  if (total === 1) {
    offsetMultiplier = 1.0;
  } else if (total === 2) {
    offsetMultiplier = index === 0 ? 1.0 : -1.0;
  } else if (total === 3) {
    offsetMultiplier = [1.0, 0, -1.0][index];
  } else {
    // 4本以上は正から負へ均等分布
    const step = 2.0 / (total - 1);
    offsetMultiplier = 1.0 - index * step;
  }

  return { offsetMultiplier, total };
}

// ============================================================================
// HexCell コンポーネント（MapDisplay.tsx スタイル）
// ============================================================================

interface HexCellProps {
  hex: EditorHex;
  centerX: number;
  centerY: number;
  hexSize: number;
  rotationAngle: number;
  isSelected: boolean;
  onClick?: () => void;
  onRightClick?: () => void;
}

function HexCell({
  hex,
  centerX,
  centerY,
  hexSize,
  rotationAngle,
  isSelected,
  onClick,
  onRightClick,
}: HexCellProps) {
  const points = getHexPoints(centerX, centerY, hexSize);

  const handleContextMenu = (e: React.MouseEvent) => {
    e.preventDefault();
    onRightClick?.();
  };

  // 都市がある場合は都市色（最初の色）、それ以外は地形色
  const fillColor = hex.city && hex.city.colors.length > 0
    ? getCityColorFill(hex.city.colors[0])
    : getTerrainFill(hex.terrain);

  return (
    <g
      className="hex-cell cursor-pointer"
      onClick={onClick}
      onContextMenu={handleContextMenu}
    >
      {/* Hex本体 - 都市の場合は都市色で塗りつぶし */}
      <polygon
        className="transition-all duration-150 hover:opacity-80"
        points={points}
        fill={fillColor}
        stroke={
          isSelected
            ? 'hsl(var(--primary))'
            : hex.city
              ? 'hsl(var(--foreground))'
              : 'hsl(var(--border))'
        }
        strokeWidth={isSelected ? 3 : (hex.city ? 2 : 1)}
      />

      {/* 都市: 中央に白丸 + ダイス番号（MapDisplay.tsx スタイル） */}
      {hex.city && (
        <g transform={`rotate(${-rotationAngle}, ${centerX}, ${centerY})`}>
          {/* 中央の円（diceColorsに応じて白/黒/両方） */}
          {hex.city.diceColors.length === 2 ? (
            // 両方の場合は半分ずつ
            <>
              <clipPath id={`clip-left-${hex.q}-${hex.r}`}>
                <rect x={centerX - hexSize} y={centerY - hexSize} width={hexSize} height={hexSize * 2} />
              </clipPath>
              <clipPath id={`clip-right-${hex.q}-${hex.r}`}>
                <rect x={centerX} y={centerY - hexSize} width={hexSize} height={hexSize * 2} />
              </clipPath>
              <circle cx={centerX} cy={centerY} r={hexSize * 0.55} fill="white" clipPath={`url(#clip-left-${hex.q}-${hex.r})`} />
              <circle cx={centerX} cy={centerY} r={hexSize * 0.55} fill="#1a1a1a" clipPath={`url(#clip-right-${hex.q}-${hex.r})`} />
              <circle cx={centerX} cy={centerY} r={hexSize * 0.55} fill="none" stroke="hsl(var(--foreground))" strokeWidth="2" />
            </>
          ) : (
            <circle
              cx={centerX}
              cy={centerY}
              r={hexSize * 0.55}
              fill={hex.city.diceColors[0] === 'black' ? '#1a1a1a' : 'white'}
              stroke="hsl(var(--foreground))"
              strokeWidth="2"
            />
          )}
          {/* ダイス番号（複数対応） */}
          {hex.city.diceNumbers.length > 0 && (
            <text
              className={`text-[14px] font-heading font-bold ${hex.city.diceColors[0] === 'black' ? 'fill-white' : 'fill-foreground'}`}
              x={centerX}
              y={centerY + 5}
              textAnchor="middle"
            >
              {hex.city.diceNumbers.join(',')}
            </text>
          )}
          {/* 都市名は別レイヤーで描画 */}
        </g>
      )}

      {/* 町表示 */}
      {hex.town && !hex.city && (
        <g transform={`rotate(${-rotationAngle}, ${centerX}, ${centerY})`}>
          <circle
            cx={centerX}
            cy={centerY}
            r={hexSize * 0.4}
            fill="white"
            stroke="hsl(var(--muted-foreground))"
            strokeWidth="1"
          />
          <text
            className="text-[7px] fill-muted-foreground"
            x={centerX}
            y={centerY + 3}
            textAnchor="middle"
          >
            {hex.town.name.substring(0, 4)}
          </text>
        </g>
      )}

      {/* 敷設済み線路 */}
      {hex.preLaidTrack && (
        <g transform={`rotate(${(hex.preLaidTrack.rotation || 0) * 60}, ${centerX}, ${centerY})`}>
          {renderTileTracks(
            hex.preLaidTrack.tileType,
            centerX,
            centerY,
            hexSize,
            false,
            false,
            false,
            undefined,
            [UNOWNED_TRACK_COLOR]
          )}
        </g>
      )}

      {/* 壁の描画（赤い太線） */}
      {hex.blockedEdges && hex.blockedEdges.length > 0 && (
        <>
          {hex.blockedEdges.map((edge) => {
            // 辺の両端の頂点を計算
            const vertex1Angle = (edge * 60) * Math.PI / 180;
            const vertex2Angle = ((edge + 1) * 60) * Math.PI / 180;
            const v1x = centerX + hexSize * Math.sin(vertex1Angle);
            const v1y = centerY - hexSize * Math.cos(vertex1Angle);
            const v2x = centerX + hexSize * Math.sin(vertex2Angle);
            const v2y = centerY - hexSize * Math.cos(vertex2Angle);

            return (
              <line
                key={`wall-${hex.q}-${hex.r}-${edge}`}
                x1={v1x}
                y1={v1y}
                x2={v2x}
                y2={v2y}
                stroke="#DC2626"
                strokeWidth="4"
                strokeLinecap="round"
              />
            );
          })}
        </>
      )}

      {/* 座標表示 */}
      <text
        className="text-[5px] fill-muted-foreground/50"
        x={centerX}
        y={centerY + hexSize * 0.7}
        textAnchor="middle"
        transform={`rotate(${-rotationAngle}, ${centerX}, ${centerY})`}
      >
        {hex.q},{hex.r}
      </text>
    </g>
  );
}

// ============================================================================
// HexGrid コンポーネント
// ============================================================================

export function HexGrid({
  hexes,
  hexSize = 30,
  selectedHex,
  onHexClick,
  onHexRightClick,
  orientation = 'portrait',
  onToggleOrientation,
  zoomLevel = 1.0,
  onZoomIn,
  onZoomOut,
  ferryLinks = [],
  adjacentLinks = [],
  linkStartHex,
  temporaryTrack,
}: HexGridProps) {
  const rotationAngle = orientation === 'portrait' ? -90 : 0;

  // パン機能用の状態
  const [panPosition, setPanPosition] = useState({ x: 0, y: 0 });
  const [isDragging, setIsDragging] = useState(false);
  const dragStartRef = useRef<{ x: number; y: number; panX: number; panY: number } | null>(null);
  const svgRef = useRef<SVGSVGElement>(null);

  // マップの境界ボックスを計算
  const { viewBoxWidth, viewBoxHeight, centerOffsetX, centerOffsetY, mapCenterX, mapCenterY } =
    useMemo(() => {
      if (hexes.length === 0) {
        return {
          viewBoxWidth: 800,
          viewBoxHeight: 600,
          centerOffsetX: 400,
          centerOffsetY: 300,
          mapCenterX: 400,
          mapCenterY: 300,
        };
      }

      let minX = Infinity, minY = Infinity;
      let maxX = -Infinity, maxY = -Infinity;

      hexes.forEach((hex) => {
        const { x, y } = axialToPixel(hex.q, hex.r, hexSize);
        minX = Math.min(minX, x);
        minY = Math.min(minY, y);
        maxX = Math.max(maxX, x);
        maxY = Math.max(maxY, y);
      });

      const padding = hexSize * 2;
      minX -= padding;
      minY -= padding;
      maxX += padding;
      maxY += padding;

      const viewBoxWidth = maxX - minX;
      const viewBoxHeight = maxY - minY;

      return {
        viewBoxWidth,
        viewBoxHeight,
        centerOffsetX: -minX,
        centerOffsetY: -minY,
        mapCenterX: viewBoxWidth / 2,
        mapCenterY: viewBoxHeight / 2,
      };
    }, [hexes, hexSize]);

  // ドラッグ開始
  const handleMouseDown = useCallback((e: React.MouseEvent) => {
    // 中ボタンまたはSpaceキー押下時のみドラッグ
    if (e.button === 1) {
      e.preventDefault();
      setIsDragging(true);
      dragStartRef.current = {
        x: e.clientX,
        y: e.clientY,
        panX: panPosition.x,
        panY: panPosition.y,
      };
    }
  }, [panPosition]);

  // ドラッグ中
  const handleMouseMove = useCallback((e: React.MouseEvent) => {
    if (!isDragging || !dragStartRef.current) return;

    const dx = e.clientX - dragStartRef.current.x;
    const dy = e.clientY - dragStartRef.current.y;

    setPanPosition({
      x: dragStartRef.current.panX + dx / zoomLevel,
      y: dragStartRef.current.panY + dy / zoomLevel,
    });
  }, [isDragging, zoomLevel]);

  // ドラッグ終了
  const handleMouseUp = useCallback(() => {
    setIsDragging(false);
    dragStartRef.current = null;
  }, []);

  // マウスホイールでズーム
  const handleWheel = useCallback((e: React.WheelEvent) => {
    e.preventDefault();
    if (e.deltaY < 0 && onZoomIn) {
      onZoomIn();
    } else if (e.deltaY > 0 && onZoomOut) {
      onZoomOut();
    }
  }, [onZoomIn, onZoomOut]);

  // パンリセット
  const handleResetPan = useCallback(() => {
    setPanPosition({ x: 0, y: 0 });
  }, []);

  return (
    <div
      className="relative w-full h-full bg-terrain-water/30 overflow-hidden"
      onMouseUp={handleMouseUp}
      onMouseLeave={handleMouseUp}
    >
      {/* コントロールパネル（右上配置） - MapDisplay.tsx スタイル */}
      <div className="absolute top-1 right-1 md:top-2 md:right-2 z-10 flex gap-0.5 md:gap-1">
        {/* 回転ボタン */}
        {onToggleOrientation && (
          <button
            className="btn-secondary px-1.5 py-0.5 md:px-2 md:py-1 text-xs min-w-[28px] md:min-w-[32px]"
            onClick={onToggleOrientation}
            title="Rotate"
          >
            {orientation === 'portrait' ? '⟳' : '⟲'}
          </button>
        )}

        {/* パンリセットボタン */}
        <button
          className="btn-secondary px-1.5 py-0.5 md:px-2 md:py-1 text-xs min-w-[28px] md:min-w-[32px]"
          onClick={handleResetPan}
          title="Reset Pan"
        >
          ⌖
        </button>

        {/* ズームボタン */}
        {onZoomOut && (
          <button
            className="btn-secondary px-1.5 py-0.5 md:px-2 md:py-1 text-xs font-bold min-w-[28px] md:min-w-[32px]"
            onClick={onZoomOut}
            title="Zoom Out"
          >
            −
          </button>
        )}
        {onZoomIn && (
          <button
            className="btn-secondary px-1.5 py-0.5 md:px-2 md:py-1 text-xs font-bold min-w-[28px] md:min-w-[32px]"
            onClick={onZoomIn}
            title="Zoom In"
          >
            +
          </button>
        )}

        {/* ズームレベル表示 */}
        <span className="hidden md:inline-block bg-card/80 px-2 py-1 rounded text-xs font-mono text-muted-foreground">
          {Math.round(zoomLevel * 100)}%
        </span>
      </div>

      {/* SVGマップ（ズーム・パン対応） */}
      <svg
        ref={svgRef}
        className={`w-full h-full ${isDragging ? 'cursor-grabbing' : ''}`}
        viewBox={`0 0 ${viewBoxWidth} ${viewBoxHeight}`}
        preserveAspectRatio="xMidYMid meet"
        style={{ overflow: 'visible', userSelect: 'none' }}
        onMouseDown={handleMouseDown}
        onMouseMove={handleMouseMove}
        onWheel={handleWheel}
      >
        {/* ズーム・パン用ラッパー */}
        <g
          transform={`translate(${mapCenterX + panPosition.x}, ${mapCenterY + panPosition.y}) scale(${zoomLevel}) translate(${-mapCenterX}, ${-mapCenterY})`}
        >
          <g transform={`rotate(${rotationAngle}, ${mapCenterX}, ${mapCenterY})`}>
            {/* フェリーリンク破線（ヘクスタイルの背面に描画） */}
            {ferryLinks.map((link) => {
              const from = axialToPixel(link.from.q, link.from.r, hexSize);
              const to = axialToPixel(link.to.q, link.to.r, hexSize);
              const fromX = from.x + centerOffsetX;
              const fromY = from.y + centerOffsetY;
              const toX = to.x + centerOffsetX;
              const toY = to.y + centerOffsetY;

              const { offsetMultiplier } = getLinkOffset(ferryLinks, link);

              const midX = (fromX + toX) / 2;
              const midY = (fromY + toY) / 2;
              const dx = toX - fromX;
              const dy = toY - fromY;
              const dist = Math.sqrt(dx * dx + dy * dy);

              const baseOffset = Math.min(dist * 0.35, 60);
              const curveOffset = baseOffset * offsetMultiplier;

              const ctrlX = midX - (dy / dist) * curveOffset;
              const ctrlY = midY + (dx / dist) * curveOffset;

              return (
                <path
                  key={`ferry-line-${link.id}`}
                  d={`M ${fromX} ${fromY} Q ${ctrlX} ${ctrlY} ${toX} ${toY}`}
                  fill="none"
                  stroke="hsl(var(--primary))"
                  strokeWidth="3"
                  strokeDasharray="8,4"
                  opacity="0.8"
                />
              );
            })}

            {/* 隣接リンク破線（ヘクスタイルの背面に描画） */}
            {adjacentLinks.map((link) => {
              const from = axialToPixel(link.from.q, link.from.r, hexSize);
              const to = axialToPixel(link.to.q, link.to.r, hexSize);
              const fromX = from.x + centerOffsetX;
              const fromY = from.y + centerOffsetY;
              const toX = to.x + centerOffsetX;
              const toY = to.y + centerOffsetY;

              return (
                <line
                  key={`adjacent-line-${link.id}`}
                  x1={fromX}
                  y1={fromY}
                  x2={toX}
                  y2={toY}
                  stroke="hsl(var(--muted-foreground))"
                  strokeWidth="2"
                  strokeDasharray="4,2"
                />
              );
            })}

            {/* ヘクス描画 */}
            {hexes.map((hex) => {
              const { x, y } = axialToPixel(hex.q, hex.r, hexSize);
              const centerX = x + centerOffsetX;
              const centerY = y + centerOffsetY;
              const isSelected = selectedHex?.q === hex.q && selectedHex?.r === hex.r;
              const isLinkStart = linkStartHex?.q === hex.q && linkStartHex?.r === hex.r;

              return (
                <HexCell
                  key={`${hex.q},${hex.r}`}
                  hex={hex}
                  centerX={centerX}
                  centerY={centerY}
                  hexSize={hexSize}
                  rotationAngle={rotationAngle}
                  isSelected={isSelected || isLinkStart}
                  onClick={() => onHexClick?.(hex)}
                  onRightClick={() => onHexRightClick?.(hex)}
                />
              );
            })}

            {/* 仮配置線路（ハイライト表示） */}
            {temporaryTrack && (() => {
              const { x, y } = axialToPixel(temporaryTrack.q, temporaryTrack.r, hexSize);
              const centerX = x + centerOffsetX;
              const centerY = y + centerOffsetY;
              return (
                <g key="temporary-track" transform={`rotate(${(temporaryTrack.rotation || 0) * 60}, ${centerX}, ${centerY})`}>
                  {renderTileTracks(
                    temporaryTrack.tileType,
                    centerX,
                    centerY,
                    hexSize,
                    true,  // isTemporary
                    false, // hasError
                    false, // isHighlighted
                    undefined,
                    undefined // primary color for temporary
                  )}
                </g>
              );
            })()}

            {/* 都市名をタイルより前面に描画（MapDisplay.tsx スタイル） */}
            {hexes.map((hex) => {
              if (!hex.city) return null;
              const { x, y } = axialToPixel(hex.q, hex.r, hexSize);
              const centerX = x + centerOffsetX;
              const centerY = y + centerOffsetY;

              return (
                <g
                  key={`cityname-${hex.q},${hex.r}`}
                  className="city-name-layer"
                  transform={`rotate(${-rotationAngle}, ${centerX}, ${centerY})`}
                  style={{ pointerEvents: 'none' }}
                >
                  <defs>
                    <path
                      id={`editorCityNamePath-${hex.q}-${hex.r}`}
                      d={`M ${centerX - hexSize * 0.7} ${centerY - hexSize * 0.3} A ${hexSize * 0.7} ${hexSize * 0.7} 0 0 1 ${centerX + hexSize * 0.7} ${centerY - hexSize * 0.3}`}
                      fill="none"
                    />
                  </defs>
                  <text className="text-[6px] fill-foreground/70 font-medium" style={{ userSelect: 'none' }}>
                    <textPath
                      href={`#editorCityNamePath-${hex.q}-${hex.r}`}
                      startOffset="50%"
                      textAnchor="middle"
                    >
                      {hex.city.name.substring(0, 12)}
                    </textPath>
                  </text>
                </g>
              );
            })}

            {/* フェリーリンクコスト表記（都市名より前面） */}
            {ferryLinks.map((link) => {
              const from = axialToPixel(link.from.q, link.from.r, hexSize);
              const to = axialToPixel(link.to.q, link.to.r, hexSize);
              const fromX = from.x + centerOffsetX;
              const fromY = from.y + centerOffsetY;
              const toX = to.x + centerOffsetX;
              const toY = to.y + centerOffsetY;

              const { offsetMultiplier } = getLinkOffset(ferryLinks, link);

              const midX = (fromX + toX) / 2;
              const midY = (fromY + toY) / 2;
              const dx = toX - fromX;
              const dy = toY - fromY;
              const dist = Math.sqrt(dx * dx + dy * dy);

              const baseOffset = Math.min(dist * 0.35, 60);
              const curveOffset = baseOffset * offsetMultiplier;

              // 制御点
              const ctrlX = midX - (dy / dist) * curveOffset;
              const ctrlY = midY + (dx / dist) * curveOffset;

              // コスト表示位置（曲線上の中点: Bezier t=0.5）
              const costX = fromX * 0.25 + ctrlX * 0.5 + toX * 0.25;
              const costY = fromY * 0.25 + ctrlY * 0.5 + toY * 0.25;

              return (
                <g key={`ferry-cost-${link.id}`} transform={`rotate(${-rotationAngle}, ${costX}, ${costY})`}>
                  <circle
                    cx={costX}
                    cy={costY}
                    r={hexSize * 0.4}
                    fill="hsl(var(--primary))"
                    stroke="white"
                    strokeWidth="2"
                  />
                  <text
                    className="text-[9px] fill-white font-bold"
                    x={costX}
                    y={costY + 3}
                    textAnchor="middle"
                  >
                    ${link.cost}
                  </text>
                </g>
              );
            })}

            {/* 隣接リンクコスト表記（都市名より前面） */}
            {adjacentLinks.map((link) => {
              const from = axialToPixel(link.from.q, link.from.r, hexSize);
              const to = axialToPixel(link.to.q, link.to.r, hexSize);
              const fromX = from.x + centerOffsetX;
              const fromY = from.y + centerOffsetY;
              const toX = to.x + centerOffsetX;
              const toY = to.y + centerOffsetY;

              const midX = (fromX + toX) / 2;
              const midY = (fromY + toY) / 2;

              return (
                <g key={`adjacent-cost-${link.id}`} transform={`rotate(${-rotationAngle}, ${midX}, ${midY})`}>
                  <circle
                    cx={midX}
                    cy={midY}
                    r={hexSize * 0.4}
                    fill="hsl(var(--accent))"
                    stroke="hsl(var(--foreground))"
                    strokeWidth="2"
                  />
                  <text
                    className="text-[9px] fill-foreground font-bold"
                    x={midX}
                    y={midY + 3}
                    textAnchor="middle"
                  >
                    ${link.cost}
                  </text>
                </g>
              );
            })}

          </g>
        </g>
      </svg>

      {/* 座標情報とヘルプ */}
      <div className="absolute bottom-2 left-2 bg-card/80 px-2 py-1 rounded text-xs text-muted-foreground">
        Hexes: {hexes.length}
        {selectedHex && ` | Selected: (${selectedHex.q}, ${selectedHex.r})`}
        <span className="ml-2 opacity-60">| Middle-click drag to pan, Wheel to zoom</span>
      </div>
    </div>
  );
}

export default HexGrid;
